import { useRef, useCallback, useEffect } from 'react';
import RecaptchaManager from '@shared/utils/RecaptchaManager';
import { RecaptchaResult } from '@shared/utils/RecaptchaManager';

const DEBUG = typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';

function debug(message: string, data?: Record<string, unknown>) {
  if (DEBUG) {
    console.log(`[useRecaptcha] ${message}`, data ?? '');
  }
}

interface UseRecaptchaOptions {
  siteKey: string;
  enabled?: boolean;
  timeout?: number;
  maxRetries?: number;
  retryDelay?: number;
  action?: string;
  onSuccess?: (token: string) => void;
  onError?: (error: string) => void;
  onRetry?: (retryCount: number) => void;
}

interface Callbacks {
  onSuccess?: (token: string) => void;
  onError?: (error: string) => void;
  onRetry?: (retryCount: number) => void;
}

function isWidgetLoaded(ref: unknown): boolean {
  return typeof (ref as Record<string, unknown>)?.executeAsync === 'function';
}

function canSyncRef(manager: RecaptchaManager | null, ref: unknown): boolean {
  return !!manager && !!ref && !manager.isReady() && isWidgetLoaded(ref);
}

function trySyncRefToManager(manager: RecaptchaManager | null, ref: unknown): void {
  if (!canSyncRef(manager, ref)) return;
  debug('Lazy syncing ref during isReady check');
  manager!.setRecaptchaRef(ref);
}

function notifySuccess(result: RecaptchaResult, callbacks: Callbacks): void {
  if (result.success && result.token) callbacks.onSuccess?.(result.token);
}

function notifyError(result: RecaptchaResult, callbacks: Callbacks): void {
  if (!result.success && result.error) callbacks.onError?.(result.error);
}

function notifyRetry(result: RecaptchaResult, callbacks: Callbacks): void {
  if (result.retryCount && result.retryCount > 0) callbacks.onRetry?.(result.retryCount);
}

function handleExecuteResult(result: RecaptchaResult, callbacks: Callbacks): void {
  notifySuccess(result, callbacks);
  notifyError(result, callbacks);
  notifyRetry(result, callbacks);
}

const DISABLED_RESULT: RecaptchaResult = { success: true, token: 'disabled' };

export const useRecaptcha = (options: UseRecaptchaOptions) => {
  const recaptchaRef = useRef<any>(null);
  const managerRef = useRef<RecaptchaManager | null>(null);
  const isDisabled = options.enabled === false || !options.siteKey;

  const callbacksRef = useRef<Callbacks>({
    onSuccess: options.onSuccess,
    onError: options.onError,
    onRetry: options.onRetry
  });

  useEffect(() => {
    callbacksRef.current = {
      onSuccess: options.onSuccess,
      onError: options.onError,
      onRetry: options.onRetry
    };
  }, [options.onSuccess, options.onError, options.onRetry]);

  useEffect(() => {
    if (isDisabled) {
      debug('Manager not initialized - disabled or no site key');
      return;
    }

    managerRef.current = RecaptchaManager.getInstance({
      siteKey: options.siteKey,
      enabled: options.enabled ?? true,
      timeout: options.timeout,
      maxRetries: options.maxRetries,
      retryDelay: options.retryDelay,
      action: options.action
    });
    debug('RecaptchaManager initialized');
  }, [options.siteKey, isDisabled, options.enabled, options.timeout, options.maxRetries, options.retryDelay, options.action]);

  useEffect(() => {
    const manager = managerRef.current;
    const ref = recaptchaRef.current;
    if (manager && ref && isWidgetLoaded(ref)) {
      debug('Syncing ref to manager');
      manager.setRecaptchaRef(ref);
    }
  });

  const executeWhenDisabled = useCallback((): RecaptchaResult => {
    debug('Execute bypassed - reCAPTCHA disabled');
    callbacksRef.current.onSuccess?.('disabled');
    return DISABLED_RESULT;
  }, []);

  const executeWithManager = useCallback(async (): Promise<RecaptchaResult> => {
    try {
      const result = await managerRef.current!.execute();
      debug('Execution result', { success: result.success, hasToken: !!result.token });
      handleExecuteResult(result, callbacksRef.current);
      return result;
    } catch (error) {
      const errorMessage = `reCAPTCHA execution failed: ${error}`;
      callbacksRef.current.onError?.(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const execute = useCallback(async (): Promise<RecaptchaResult> => {
    if (isDisabled) return executeWhenDisabled();

    if (!managerRef.current) {
      const error = 'reCAPTCHA not initialized';
      callbacksRef.current.onError?.(error);
      return { success: false, error };
    }

    return executeWithManager();
  }, [isDisabled, executeWhenDisabled, executeWithManager]);

  const reset = useCallback(() => {
    managerRef.current?.reset();
  }, []);

  const isReady = useCallback(() => {
    if (isDisabled) return true;

    const manager = managerRef.current;
    if (!manager) return false;
    const readyState = manager.isReady();
    if (!readyState) {
      trySyncRefToManager(manager, recaptchaRef.current);
    }
    return manager.isReady();
  }, [isDisabled]);

  return {
    recaptchaRef,
    execute,
    reset,
    isReady
  };
};

export default useRecaptcha;
